import React, { useMemo } from 'react';
import { PARTNER_ROLE_DEFS } from '../constants/partnerRoleDefs.js';

const ROLE_ORDER = new Map(PARTNER_ROLE_DEFS.map((d, i) => [d.key, i]));
const ROLE_DEF_BY_KEY = new Map(PARTNER_ROLE_DEFS.map(d => [d.key, d]));

function normalizeKeys(roleKeys) {
  const raw = Array.isArray(roleKeys) ? roleKeys : [];
  const uniq = [...new Set(raw.map(k => String(k || '').trim()).filter(Boolean))];
  uniq.sort((a, b) => (ROLE_ORDER.get(a) ?? 99) - (ROLE_ORDER.get(b) ?? 99));
  return uniq;
}

/**
 * Badges visuales de roles partner (emoji + label desde partnerRoleDefs).
 * @param {string[]|null|undefined} roleKeys — p. ej. profiles.partner_active_roles
 * @param {'light'|'dark'} theme
 * @param {boolean} compact — solo emoji; label en title (tooltip nativo)
 */
export default function PartnerRoleBadges({ roleKeys, compact = false, theme = 'light', style, className }) {
  const keys = useMemo(() => normalizeKeys(roleKeys), [roleKeys]);

  if (!keys.length) return null;

  const isDark = theme === 'dark';
  const pillBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(83,74,183,0.09)';
  const pillBorder = isDark ? '0.5px solid rgba(255,255,255,0.12)' : '1px solid rgba(83,74,183,0.18)';
  const textColor = isDark ? 'rgba(255,255,255,0.88)' : '#3d3677';

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: compact ? 4 : 6,
        alignItems: 'center',
        ...style,
      }}
    >
      {keys.map(key => {
        const def = ROLE_DEF_BY_KEY.get(key);
        const label = def?.label ?? key;
        const emoji = def?.emoji ?? '';
        return (
          <span
            key={key}
            title={label}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: compact ? 0 : 4,
              fontSize: compact ? '0.62rem' : '0.66rem',
              fontWeight: 600,
              lineHeight: 1.2,
              color: textColor,
              background: pillBg,
              border: pillBorder,
              borderRadius: 999,
              padding: compact ? '2px 6px' : '3px 8px',
              maxWidth: '100%',
            }}
          >
            <span aria-hidden="true">{emoji}</span>
            {!compact ? <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span> : null}
          </span>
        );
      })}
    </div>
  );
}
